import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import CountryList from 'country-list-with-dial-code-and-flag';
import ReactCountryFlag from 'react-country-flag';

const CountrySelect = ({
  defaultValue,
  onValueChange = () => {},
  labelKey = 'label',
  valueKey = 'value',
}) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Memoize the full country list once
  const allCountries = useMemo(() => {
    return CountryList.getAll().map((d) => ({
      label: d.data[labelKey],
      value: d.data[valueKey],
      code: d.data.code,
      currency: d.data.currency,
      currency_code: d.data.currency_code,
      currency_symbol: d.data.currency_symbol,
      dial_code: d.data.dial_code,
      flag: d.data.flag,
      local_name: d.data.local_name,
    }));
  }, [labelKey, valueKey]);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);

  const [value, setValue] = useState(() => {
    return allCountries.find((a) => a.value === defaultValue) || null;
  });

  const filteredCountries = useMemo(() => {
    const filtered = search
      ? allCountries.filter((c) =>
          c.label.toLowerCase().includes(search.toLowerCase())
        )
      : allCountries;

    setFocusedIndex(0);
    return filtered;
  }, [search, allCountries]);

  const onChange = (val) => {
    setValue(val);
    onValueChange(val);
  };

  const handleKeyDown = (e) => {
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => Math.min(prev + 1, filteredCountries.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCountries[focusedIndex]) {
        onChange(filteredCountries[focusedIndex]);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  useEffect(() => {
    const list = dropdownRef.current;
    if (list) {
      const child = list.children[focusedIndex];
      if (child) {
        child.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !dropdownRef.current?.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box
      className="mb-3"
      sx={{ position: 'relative', width: '100%' }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <Box
        ref={inputRef}
        className="input"
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          px: 2,
          py: 0.8,
          cursor: 'pointer',
          minHeight: 30,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value ? (
          <Typography fontSize={13} sx={{ display: 'flex', alignItems: 'center' }}>
            <ReactCountryFlag countryCode={value.code} svg style={{ marginRight: 8 }} />
            {value.label}
          </Typography>
        ) : (
          <Typography fontSize={12} color="textSecondary">
            Select
          </Typography>
        )}
      </Box>

      {open && (
        <Box
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            top: '100%',
            zIndex: 10,
            width: '100%',
            background: '#fff',
            borderRadius: 2,
            boxShadow: 3,
            maxHeight: 200,
            overflowY: 'auto',
          }}
        >
          <InputBase
            placeholder="Search country..."
            fullWidth
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              px: 1,
              mb: 1,
              borderRadius: 1,
              fontSize: 14,
            }}
          />
          <Box>
            {filteredCountries.map((country, idx) => (
              <Box
                key={idx}
                onClick={() => {
                  onChange(country);
                  setOpen(false);
                }}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: idx === focusedIndex ? '#e0e7ff' : 'transparent',
                  '&:hover': { backgroundColor: '#f3f6fc' },
                }}
              >
                <ReactCountryFlag
                  countryCode={country.code}
                  svg
                  style={{ marginRight: 8 }}
                />
                {country.label}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CountrySelect;
